import { create, v } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/widgets/middleware/theme';
import { RenderResult } from '@dojo/framework/core/interfaces';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import dimensions from '@dojo/framework/core/middleware/dimensions';

import { Keys } from '@holmescorp/common';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

import { isVariantTitle } from '../utils';
import { VariantTitle } from '../interfaces';

import css from '../css/accordion2.m.css';

export interface AccordionPaneProperties {
	/** Set the heading level of the TitlePanes */
	headingLevel?: number;
	/** Inform the widget it is part of a symantic list */
	role?: string | null;
	/** Explicitly control the pane */
	open?: boolean;
	/** */
	initialOpen?: boolean;
	/** Called when the title of an open pane is clicked */
	onClose?(): void;
	/** Called when the title of a closed pane is clicked */
	onOpen?(): void;
	/** boolean whether to wrap pane title/content in a div */
	useContainer?: boolean;
}

export interface TitlePaneChildren {
	title: RenderResult | VariantTitle;
	content: RenderResult;
}

interface AccordionPaneIcache {
	initialOpen: boolean;
	open: boolean;
}

const icache = createICacheMiddleware<AccordionPaneIcache>();
const factory = create({ icache, dimensions, theme })
	.properties<AccordionPaneProperties>()
	.children<TitlePaneChildren>();

export const AccordionPane = factory(({ middleware: { icache, dimensions, theme }, properties, children, id }) => {
	const { key, headingLevel, role, initialOpen = false, onOpen, onClose, useContainer } = properties();
	let { open } = properties();
	let { title, content } = children()[0] || ({} as TitlePaneChildren);
	if (isVariantTitle(title)) {
		title = open ? title.open : title.closed;
	}
	if (!Array.isArray(title)) {
		title = [title];
	}
	if (!Array.isArray(content)) {
		content = [content];
	}
	const themeCss = theme.classes(css);

	// const firstRender = icache.get('open') === undefined;
	if (open === undefined) {
		open = icache.get('open');
		const existingInitialOpen = icache.get('initialOpen');

		if (initialOpen !== existingInitialOpen) {
			icache.set('open', initialOpen);
			icache.set('initialOpen', initialOpen);
			open = initialOpen;
		}
	}

	// aria
	const controls = `${id}-content`;
	const expanded = open ? 'true' : 'false';
	const level = headingLevel ? `${headingLevel}` : null;
	const hidden = open ? null : 'true';
	const labelledby = `${id}-title`;

	const pane = [
		v(
			'div',
			{
				key: `${key}-head`,
				id: `${id}-title`,
				...formatAriaProperties({ controls, expanded }),
				role: 'button',
				tabIndex: 0,
				classes: [themeCss.heading, 'accordion-tab', open ? 'accordion-tab-selected' : ''],
				onclick: (event: MouseEvent) => {
					event.stopPropagation();
					icache.set('open', !open);
					if (open) {
						onClose && onClose();
					} else {
						onOpen && onOpen();
					}
				},
				onkeydown: (event: KeyboardEvent) => {
					switch (event.key) {
						case Keys.Enter:
						case Keys.Space:
						case Keys.Spacebar:
							event.preventDefault();
							icache.set('open', !open);
							if (open) {
								onClose && onClose();
							} else {
								onOpen && onOpen();
							}
					}
				}
			},
			title
		),
		open
			? v(
					'section',
					{
						id: `${id}-content`,
						...formatAriaProperties({ labelledby }),
						enterAnimation: themeCss.enter,
						exitAnimation: themeCss.exit,
						classes: [themeCss.body, 'accordion-content']
					},
					content
			  )
			: v('div', {
					...formatAriaProperties({ hidden, labelledby }),
					id: `${id}-content`,
					key: 'content',
					style: { marginTop: open ? '0px' : `-${dimensions.get('content').offset.height}px` }
			  })
	];

	return useContainer
		? v('article', { key, role, classes: [themeCss.pane] }, [
				v(
					'div',
					{ role: 'heading', ...formatAriaProperties({ level }), classes: ['accordion-container'] },
					pane
				)
		  ])
		: pane;
});
